/**
 * Where the player's rounds are drawn leaving the aeroplane.
 *
 * ## The airframe muzzle, which is now the answer
 *
 * The `<PLANE>.DAT` gun block carries a real **muzzle position** per gun
 * station — signed right / nose-forward / up, in the aircraft's own `.TBL`
 * model units and against the `.TBL` origin. It is transcribed for all 33
 * aircraft in `packages/engine`'s `aircraft-gun-table.ts`, converted to
 * body-frame metres by `aircraft-model-frame.ts` (recentre onto the drawn
 * mesh's bounding box, ÷65, then the `(x, y, z) → (x, z, −y)` model→world
 * pitch), and handed to `resolveGunnery` as the point each round is spawned
 * at. So the *simulation itself* now fires from the gun ports, in every view,
 * at every window size, for the player and for every AI aeroplane — which the
 * screen registration below could never do, because it is derived from the
 * player's own cockpit art and no other aeroplane's is loaded.
 *
 * What is left for this module is therefore a **statement of the invariant**
 * plus one correction. `breechOffsets` answers "where should the rounds be
 * drawn leaving?" and `barrelOffsets` answers "where did the sim fire them
 * from?"; `bullet-sprites.ts` subtracts one from the other (never adds a
 * delta — docs/engineering/lessons.md, gunfire-geometry lane, rule 3). With
 * nothing to correct for those two are the same call, so the shift is exactly
 * zero and the drawn round *is* the fired round; a unit test pins that rather
 * than leaving it to inspection.
 *
 * ## Why the player's own cockpit still gets a screen registration
 *
 * Because the painted panel and the 3D camera did **not share a principal
 * point**, and the airframe muzzle could not fix that. Measured for the Camel
 * *before* `cockpit-projection.ts` landed (its gunsight ring is at logical
 * `(159, 80)`; the muzzle sits 2.723 m ahead of the eye and 0.196 m above it,
 * i.e. 4.12° above the boresight):
 *
 * | window | panel rect | gunsight | boresight | drawn round | round − sight |
 * |---|---|---|---|---|---|
 * | 1280x720 | 640x400 @(320,320) | y 480 | y 360 | y 310 | **−170 px** |
 * | 1024x768 | 640x400 @(192,368) | y 528 | y 384 | y 331 | **−197 px** |
 * | 1600x900 | 960x800 @(320,100) | y 420 | y 450 | y 388 | −32 px |
 * | 1920x1080 | 1280x1000 @(320,80) | y 480 | y 540 | y 465 | −15 px |
 *
 * Almost all of that is `gunsight − H/2`: the panel is presented at an integer
 * scale anchored bottom-centre (AD-009), so its optical centre is 120 px
 * *below* the camera's at 720p and 60 px *above* it at 1080p, while the
 * camera's principal point is always the window centre. The muzzle's own
 * 4.12° contributes only 50–75 px of it.
 *
 * Note what that means for the *stream* rather than the spawn point: a round
 * flies parallel to the boresight, so however the muzzle moves, the far end of
 * a burst converges on the camera's principal point and nowhere else. Moving
 * the muzzle by 20 cm moves a round 100 m out by about **one pixel**. The
 * muzzle therefore fixes where a burst *starts* and can do nothing at all
 * about where it *goes* — which is why this table is a presentation finding,
 * not a geometry one, and why it reads the same before and after the `.DAT`
 * transcription.
 *
 * **The cure was the camera, not the muzzle** — and it has been applied.
 * `cockpit-projection.ts` shears the cockpit camera's principal point onto the
 * painted gunsight with `setViewOffset`, so the boresight *is* the sight at
 * every window size and a burst converges on the ring the player is aiming
 * through. It also recovers *why* row 80 is the right row: all 33
 * `<PLANE>.DAT` files carry the original's own 3D window rect, `x 0..319,
 * y 0..160`, whose centre is exactly where the Camel's ring is painted.
 *
 * The registration below survives that fix rather than being replaced by it:
 * the camera and the panel now share an optical centre but still not an
 * angular scale, so the *painted breeches* — which are 57 logical pixels off
 * that centre, not on it — still have to be unprojected through the presented
 * panel. It lands the *start* of the burst on the painted port at every size
 * by construction, and it is the only consumer that needs it, because the
 * sim's own muzzle is airframe-anchored for every aeroplane in the sky.
 *
 * The registration is recovered geometry too — the pixels the aircraft's own
 * `.TTM` draws the muzzle flash at — but it is a *screen* position, so it has
 * to be converted by inverting the projection that actually put it on screen:
 *
 * 1. **Painted panel → viewport.** The 320x200 composition is presented at an
 *    integer scale anchored bottom-centre, *not* stretched over the window
 *    (AD-009), so a logical pixel's screen position comes from
 *    `presentTransform` and from nothing else — least of all from the 3D
 *    camera's aspect ratio.
 * 2. **Viewport → camera frame.** Unproject that NDC through the 3D camera at
 *    the depth the sim's own muzzle sits at. In the forward glance the cockpit
 *    camera's basis *is* the body basis (`cockpit-view.ts`'s
 *    `cockpitCameraView` builds it from `BODY_FORWARD`/`BODY_UP`), so the
 *    result is already body-frame — right +X, up +Y, forward −Z.
 * 3. **Eye → pose origin → world.** The ray is measured from the pilot's eye
 *    (`cockpitEyePosition`) while a round's position is measured from the pose
 *    origin, so the eye offset is added back before the single
 *    `pose.orientation` rotation into world space.
 *
 * Its cost is the reason it is no longer the *source of truth*: a registration
 * against a painted panel moves by a few centimetres when the window is
 * resized, and it only exists for the aircraft the player is sitting in. It
 * is now a correction applied on top of a real muzzle rather than a stand-in
 * for one.
 *
 * The math is pure and lives in the free functions below; the class holds only
 * the two pieces of state that outlive a call — the loaded composition's
 * muzzle points and the viewport they are registered against.
 */

import {
  addVec3,
  barrelBodyOffset,
  gunForwardM,
  rotateVec3,
  type GunSpec,
  type Pose,
  type Quat,
  type Tunables,
  type Vec3,
} from "@red-opus/engine";
import type { CockpitPoint } from "@red-opus/baker/schema";
import { MUZZLE_FLASH_SIZE, muzzleEyeOffsets, type MuzzleViewport } from "./bullet-sprites.ts";
import { loadCockpitMuzzles } from "./cockpit-composition.ts";
import { DEFAULT_EYE_OFFSET, type EyeOffset } from "./cockpit-view.ts";

/** The gun geometry the sim used, so the drawn muzzle can be expressed as a difference from it. */
export interface GunGeometry {
  readonly gun: GunSpec;
  readonly tunables: Tunables;
}

/** True when the spec carries a `.DAT` gun position, which every one of the 33 shipped aircraft does. */
export function hasAirframeMuzzle(geometry: GunGeometry): boolean {
  return geometry.gun.forwardMount !== undefined;
}

/**
 * Metres ahead of the pilot's eye that a *painted* breech is placed at:
 * exactly the depth the sim's own muzzle sits at, so registering a breech
 * shifts a round *across* its flight path and never along it. Derived rather
 * than chosen — a constant of its own could only ever disagree with the sim,
 * which is how a previous 1.4 m against the engine's 3.0 m halved the spread.
 * The airframe mount supplies the depth when there is one (the Camel's is
 * 3.023 m, against the 3.0 m fallback); `gunForwardM` covers the rest.
 */
export function breechDepthM(geometry: GunGeometry, eye: EyeOffset = DEFAULT_EYE_OFFSET): number {
  const mount = geometry.gun.forwardMount;
  const forwardM = mount ? -mount.bodyOffsetM.z : gunForwardM(geometry.tunables);
  return Math.max(forwardM - eye.forwardM, 0.1);
}

/** The pilot's eye in the aircraft's body frame — `cockpit-view.ts`'s offset as a vector. */
function eyeBodyOffset(eye: EyeOffset): Vec3 {
  return { x: eye.rightM, y: eye.upM, z: -eye.forwardM };
}

/**
 * The **body-frame** position of each painted gun breech, relative to the
 * aircraft's pose origin: the eye, plus the ray through the painted muzzle
 * flash, run out to the fallback muzzle depth.
 */
export function breechBodyOffsets(
  muzzles: readonly CockpitPoint[],
  viewport: MuzzleViewport,
  geometry: GunGeometry,
  eye: EyeOffset = DEFAULT_EYE_OFFSET,
): Vec3[] {
  const eyeBody = eyeBodyOffset(eye);
  return muzzleEyeOffsets(muzzles, viewport, breechDepthM(geometry, eye), MUZZLE_FLASH_SIZE).map((offset) =>
    addVec3(eyeBody, offset),
  );
}

/** World-space deltas from the pose origin to each painted breech. */
export function breechWorldOffsets(
  muzzles: readonly CockpitPoint[],
  viewport: MuzzleViewport,
  orientation: Quat,
  geometry: GunGeometry,
  eye: EyeOffset = DEFAULT_EYE_OFFSET,
): Vec3[] {
  return breechBodyOffsets(muzzles, viewport, geometry, eye).map((body) => rotateVec3(orientation, body));
}

/**
 * World-space deltas from the pose origin to each of the *sim's* barrels —
 * exactly what `resolveGunnery` already put into every `RoundRecord.from`, so
 * subtracting it is what keeps a drawn muzzle an absolute position rather than
 * a second offset stacked on the first.
 */
export function barrelWorldOffsets(orientation: Quat, geometry: GunGeometry): Vec3[] {
  const barrels = Math.max(1, Math.round(geometry.gun.barrels || 1));
  return Array.from({ length: barrels }, (_, index) =>
    rotateVec3(orientation, barrelBodyOffset(index, geometry.gun, geometry.tunables)),
  );
}

/**
 * Holds the loaded cockpit's muzzle points and the viewport they are
 * registered against. A class rather than a closure factory: it holds state
 * that outlives a call, and `docs/engineering/lessons.md`'s multiplayer entry
 * records where the closure-factory shape stops paying.
 */
export class GunMuzzles {
  private points: readonly CockpitPoint[] = [];
  private viewport: MuzzleViewport = { widthPx: 1, heightPx: 1, fovDeg: 55 };

  /** Best-effort: with no baked cockpits the list stays empty and the fallback has nothing to offer. */
  async load(aircraftKey: string): Promise<void> {
    try {
      this.points = await loadCockpitMuzzles(aircraftKey);
    } catch {
      this.points = [];
    }
  }

  /**
   * The viewport the painted panel is being presented into. Only the fallback
   * path reads it, and it must be re-set on every resize: the panel's integer
   * scale and bottom-centre anchor put the painted flash at a different place
   * in the frustum on every window size, so a stale viewport is a stale
   * registration.
   */
  setViewport(viewport: MuzzleViewport): void {
    this.viewport = viewport;
  }

  /** True once a cockpit composition with at least one painted muzzle flash has loaded. */
  get loaded(): boolean {
    return this.points.length > 0;
  }

  /**
   * Where the rounds should be drawn leaving.
   *
   * With a cockpit composition loaded this is the painted-port registration —
   * the panel/camera principal-point correction the module doc measures. With
   * none (every aeroplane but the player's own) it is the airframe muzzle
   * itself, which is the same call `resolveGunnery` made, so the round is
   * already there and `muzzleShiftFor` resolves to zero.
   */
  breechOffsets(pose: Pose, geometry: GunGeometry, eye?: EyeOffset): readonly Vec3[] {
    if (this.points.length > 0) {
      return breechWorldOffsets(this.points, this.viewport, pose.orientation, geometry, eye);
    }
    return hasAirframeMuzzle(geometry) ? this.barrelOffsets(pose, geometry) : [];
  }

  barrelOffsets(pose: Pose, geometry: GunGeometry): readonly Vec3[] {
    return barrelWorldOffsets(pose.orientation, geometry);
  }
}
